import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import {
  enquirySourceOptions,
  PaginationLimit,
  propertyFeatures,
} from "../../Config";
import tw from "tailwind-styled-components";
import Images from "../../Images";
import {
  PrevBtn,
  NextBtn,
  PaginationWrapper,
  UserImage,
} from "../Styles/PageStyles";
import { MdModeEditOutline, MdDelete } from "react-icons/md";
import { FiChevronRight } from "react-icons/fi";
import {
  capitalizeFirstLetter,
  getFurnishedLabel,
  getRentRangeLabel,
  getTenantPreferenceLabel,
  handleCopy,
  truncateText,
} from "../../helpers";

import { IoCopySharp } from "react-icons/io5";
import Tooltip from "../Tooltip";
import { useSelector } from "react-redux";
import { FaFileAlt } from "react-icons/fa";

const Tables = ({
  apiData,
  setSelectedData,
  setIsEditEnquiries,
  setIsDeleteEnquiries,
  getInventoryListMutate,
  setInventoryList,
  setIsMatchDrawerOpen,
  setIsViewNotes,
}) => {
  const locations = useSelector((state) => state.LocationsReducer.locations);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [currentPage, setCurrentPage] = useState(0);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [data, setData] = useState([
    {
      deviceName: "",
      extractionTime: "",
      processingTime: "",
      drops: "",
      action: "",
    },
  ]);

  const edit = (item) => {
    setIsEditEnquiries(true);
    setSelectedData(item);
    setOpenActionMenu(null);
  };

  const deleteEnquiries = (item) => {
    setIsDeleteEnquiries(true);
    setSelectedData(item);
  };

  const showProps = (item) => {
    setSelectedData(item);
    setInventoryList([]); // clear old data
    setIsMatchDrawerOpen(true);
    getInventoryListMutate({ id: item.id });
  };

  const isNotes = (item) =>{
    setSelectedData(item);
    setIsViewNotes(true);
    setOpenActionMenu(null);
  }

  const ActionBtns = (item) => (
    <div className="flex items-center gap-3">
      <Tooltip label="Click to view and add notes">
        <button
          onClick={(e) => {
            e.stopPropagation();
            isNotes(item);
          }}
          className="text-gray-400 hover:text-blue-600 transition bg-gray-50 rounded-full p-1.5"
        >
          <FaFileAlt size={16} />
        </button>
      </Tooltip>

      <Tooltip label="Click to edit the enquiries">
        <button
          onClick={() => edit(item)}
          className="text-gray-400 hover:text-gray-700 transition bg-gray-50 rounded-full p-1.5"
          // title="Edit Location"
        >
          <MdModeEditOutline size={16} />
        </button>
      </Tooltip>

      <Tooltip label="Click to delete the enquiries">
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteEnquiries(item);
          }}
          className="text-gray-400 hover:text-rose-600 transition bg-gray-50 rounded-full p-1.5"
        >
          <MdDelete size={16} />
        </button>
      </Tooltip>
    </div>
  );

  const toggleRow = (index) => {
    setSelectedRow(selectedRow === index ? null : index);
  };
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getName = (item) => {
    const name = capitalizeFirstLetter(item?.name);
    return (
      <div className="flex items-center gap-2">
        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
          <UserImage src={Images.Avatar} alt={`Avatar`} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-600 truncate max-w-[140px]">
              {truncateText(name, 25)}
            </span>
          </div>

          <div className="flex items-center gap-1 text-xs text-gray-400">
            #{item.mobile}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(item.mobile);
              }}
              className="hover:text-gray-600 transition"
              title="Copy Number"
            >
              <IoCopySharp size={12} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getInventories = (item) => {
    // If no inventories → show only number, no arrow, no click
    if (!item.matchingCount || item.matchingCount === 0) {
      return (
        <span className="px-2.5 py-1 text-xs bg-gray-50 text-gray-400 rounded-full border">
          No Match
        </span>
      );
    }

    // If inventories exist → show clickable button with arrow
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => showProps(item)}
          className="
            px-3 py-1.5
            text-sm font-medium
            text-indigo-600
            bg-indigo-50
            hover:bg-indigo-100
            rounded-full
            flex items-center gap-1.5
            transition
          "
        >
          {item.matchingCount} Matches
          <FiChevronRight size={14} />
        </button>
      </div>
    );
  };

  const getRentRange = (item) => {
    const value = item?.rentRange || "";
    const label = getRentRangeLabel(Number(value));

    const locationName = locations?.find(
      (loc) => loc.id === item?.locationId,
    )?.name;

    const sourceLabel = enquirySourceOptions?.find(
      (s) => s.value === item.source,
    )?.label;

    // Preferences
    let pref = [];
    try {
      pref = item?.preferences ? JSON.parse(item.preferences) : [];
    } catch {
      pref = [];
    }

    const prefLabels = propertyFeatures
      .filter((f) => pref.includes(f.value))
      .map((f) => f.label)
      .join(", ");

    const furnishedLabel = getFurnishedLabel(item?.furnishingType);
    const tenantLabel = getTenantPreferenceLabel(item?.tenantPreference);

    const isValid = (val) => val && val !== "-";

    const fields = [
      isValid(locationName) && (
        <span>
          <strong>Location:</strong> {locationName}
        </span>
      ),
      isValid(sourceLabel) && (
        <span>
          <strong>Source:</strong> {sourceLabel}
        </span>
      ),
      isValid(prefLabels) && (
        <span>
          <strong>Preference:</strong> {prefLabels}
        </span>
      ),
      isValid(furnishedLabel) && (
        <span>
          <strong>Furnished Type:</strong> {furnishedLabel}
        </span>
      ),
      isValid(tenantLabel) && (
        <span>
          <strong>Tenant Preference:</strong> {tenantLabel}
        </span>
      ),
    ].filter(Boolean);

    const content = (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
        {label || "-"}
      </span>
    );

    if (fields.length === 0) return content;

    return (
      <Tooltip
        label={
          <div className="flex flex-col text-xs text-left gap-0.5">
            {fields}
          </div>
        }
      >
        {content}
      </Tooltip>
    );
  };

  const getLocation = (item) => {
    const locationName = locations?.find(
      (loc) => loc.id === item?.locationId,
    )?.name;
    const local = locationName || "-";

    const sourceLabel = enquirySourceOptions?.find(
      (s) => s.value === item.source,
    )?.label;

    return (
      <div className="flex flex-col">
        <span className="text-gray-600 text-sm">{truncateText(local, 25)}</span>

        {/* Source */}
        {sourceLabel && (
          <span className="text-[11px] text-gray-400">{sourceLabel}</span>
        )}
      </div>
    );
  };

  const getPreference = (item) => {
    let pref = [];

    try {
      pref = item?.preferences ? JSON.parse(item.preferences) : [];
    } catch (e) {
      pref = [];
    }

    if (!pref.length) {
      return (
        <span className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded-md border">
          No Preference
        </span>
      );
    }

    const selected = propertyFeatures.filter((f) => pref.includes(f.value));

    const visible = selected.slice(0, 2);
    const remaining = selected.length - visible.length;

    return (
      <div className="flex items-center gap-1.5 flex-wrap max-w-[180px]">
        {visible.map((f) => (
          <span
            key={f.value}
            className="
            px-2.5 py-1 
            text-[11px] font-medium 
            rounded-full 
            bg-white
            text-gray-700
            border border-gray-200
            shadow-[0_1px_2px_rgba(0,0,0,0.04)]
          "
          >
            {f.label}
          </span>
        ))}

        {remaining > 0 && (
          <Tooltip label={selected.map((f) => f.label).join(", ")}>
            <span
              className="
            px-2 py-1 
            text-[11px] 
            rounded-full 
            bg-indigo-50 
            text-indigo-600 
            font-medium
            cursor-pointer
          "
            >
              +{remaining}
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (apiData) {
      const tempData = apiData.map((item) => ({
        name: getName(item),
        rentRange: getRentRange(item),
        address: getLocation(item),
        preference: getPreference(item),
        action: ActionBtns(item),
        match: getInventories(item),
      }));
      setData(tempData);
    }
  }, [apiData, openActionMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("#menuOptions") &&
        !event.target.closest(".action-btn")
      ) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const columns = useMemo(() => {
    if (isMobileView) {
      return [
        {
          Header: "Name",
          accessor: "name",
        },
        {
          Header: "Rent Range",
          accessor: "rentRange",
        },
      ];
    }
    return [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Rent Range",
        accessor: "rentRange",
      },
      // {
      //   Header: "Location",
      //   accessor: "address"
      // },
      {
        Header: "Matches",
        accessor: "match",
      },
      {
        Header: "Preference",
        accessor: "preference",
      },
      {
        Header: "Action",
        accessor: "action",
      },
    ];
  }, [isMobileView]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    pageOptions,
    page,
    state: { pageIndex },
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
  } = useTable(
    {
      data,
      columns,
      initialState: { pageIndex: currentPage, pageSize: PaginationLimit },
    },
    useSortBy,
    usePagination,
  );
  useEffect(() => {
    setCurrentPage(pageIndex); // Update current page on state change
  }, [pageIndex]);
  return (
    <>
      <CustomTable {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <Th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted ? (
                      <img
                        src={Images.Arrow}
                        alt="down arrow"
                        className={`${
                          column.isSortedDesc ? "-rotate-90" : "rotate-90"
                        } w-1.5 inline-block ml-1.5`}
                      />
                    ) : (
                      ""
                    )}
                  </span>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody
          {...getTableBodyProps()}
          hasdata={(apiData?.length > 0).toString()}
        >
          {page.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <React.Fragment key={rowIndex}>
                <Tr
                  {...row.getRowProps({ style: { height: "80px" } })}
                  hasdata={(apiData?.length > 0).toString()}
                >
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <Td
                        {...cell.getCellProps()}
                        onClick={() => {
                          if (
                            cellIndex === 0 ||
                            (cellIndex === 1 && window.innerWidth < 768)
                          ) {
                            toggleRow(rowIndex);
                          }
                        }}
                      >
                        {cell.render("Cell")}
                      </Td>
                    );
                  })}
                </Tr>
                {selectedRow === rowIndex && window.innerWidth < 768 && (
                  <Tr>
                    <Td colSpan={columns.length}>
                      <ul className="flex flex-col gap-2">
                        <li>
                          <strong>Role:</strong> {data[rowIndex].Role}
                        </li>

                        <li className="flex gap-2">
                          <strong>Attendance Status:</strong>{" "}
                          {data[rowIndex].attendanceStatus}
                        </li>
                        <li className="flex gap-2">
                          <strong>Check In:</strong> {data[rowIndex].checkIn}
                        </li>
                        <li>
                          <strong>Action:</strong> {data[rowIndex].action}
                        </li>
                      </ul>
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            );
          })}
        </Tbody>
      </CustomTable>
      {apiData?.length > 0 && (
        <PaginationWrapper>
          <div className="px-2">
            Page{" "}
            <em>
              {pageIndex + 1} of {pageOptions.length}
            </em>
          </div>
          <div className="flex gap-1">
            <PrevBtn onClick={() => previousPage()} disabled={!canPreviousPage}>
              <img src={Images.Arrow} alt="arrow" className="w-2 opacity-75 " />
            </PrevBtn>
            <NextBtn onClick={() => nextPage()} disabled={!canNextPage}>
              <img
                src={Images.Arrow}
                alt="arrow"
                className="w-2 rotate-180 opacity-75 "
              />
            </NextBtn>
          </div>
        </PaginationWrapper>
      )}
    </>
  );
};

const Td = tw.td`p-3 text-sm md:p-3 text-left`;
const Th = tw.th`text-left text-sm md:p-3 p-0.5 font-normal text-gray-600`;
const CustomTable = tw.table` w-full text-zinc-600`;
const Thead = tw.thead`border-b uppercase text-gray-600 bg-blue-50`;
const Tbody = tw.tbody`${(props) => (props.hasdata === "true" ? "border-b" : "")}`;
const Tr = tw.tr`
  border-b
  hover:bg-slate-50
  transition
`;
export default Tables;
